if (!customElements.get('gallery-viewer')) {
  class GalleryViewer extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.initialised = true;
      this.scrollContainer = this.querySelector('.gallery-viewer__scroll');
      this.items = [...this.querySelectorAll('.gallery-viewer__item')];
      this.arrowButtons = [...this.querySelectorAll('.gallery-viewer__arrow')];
      this.scrollCue = this.querySelector('.gallery-viewer__scroll-cue');
      this.scrollCueText = this.querySelector('.gallery-viewer__scroll-cue-text');
      this.currentCount = this.querySelector('.gallery-viewer__current');
      this.totalCount = this.querySelector('.gallery-viewer__total');
      this.currentItem = null;
      this.scrollTicking = false;
      this.handleScrollEvent = this.handleScroll.bind(this);
      this.handleItemClickEvent = this.handleItemClick.bind(this);
      this.handleArrowClickEvent = this.handleArrowClick.bind(this);
      this.handleKeyupEvent = this.handleKeyup.bind(this);
      this.scrollContainer.addEventListener('scroll', this.handleScrollEvent, { passive: true });
      this.scrollContainer.addEventListener('click', this.handleItemClickEvent);
      this.arrowButtons.forEach((button) => button.addEventListener('click', this.handleArrowClickEvent));
      this.addEventListener('keyup', this.handleKeyupEvent);
    }

    init(currentFullUrl) {
      const visibleItems = this.getVisibleItems();
      const requestedItem = visibleItems.find((item) => item.dataset.zoomUrl === currentFullUrl);
      const item = requestedItem || visibleItems[0];
      if (!item) return;

      this.setActiveItem(item);
      this.scrollToItemPosition(item, false);
      this.updateScrollCue();

      requestAnimationFrame(() => {
        this.scrollToItemPosition(item, false);
        this.updateScrollCue();
      });
    }

    getVisibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    setActiveItem(item) {
      const visibleItems = this.getVisibleItems();
      const index = visibleItems.indexOf(item);
      if (index < 0) return;

      this.currentItem = item;
      this.items.forEach((galleryItem) => {
        const isActive = galleryItem === item;
        galleryItem.classList.toggle('is-active', isActive);
        if (isActive) {
          galleryItem.setAttribute('aria-current', 'true');
        } else {
          galleryItem.removeAttribute('aria-current');
        }
      });
      this.currentCount.textContent = index + 1;
      this.totalCount.textContent = visibleItems.length;
      this.arrowButtons.forEach((button) => {
        const direction = button.dataset.galleryDirection;
        button.disabled = direction === 'previous' ? index === 0 : index === visibleItems.length - 1;
      });
    }

    handleScroll() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;
      requestAnimationFrame(() => {
        this.updateScrollCue();
        const visibleItems = this.getVisibleItems();
        const marker = this.scrollContainer.scrollTop + this.scrollContainer.clientHeight * 0.25;
        const currentItem = visibleItems.reduce((activeItem, item) => (
          item.offsetTop <= marker ? item : activeItem
        ), visibleItems[0]);

        if (currentItem && currentItem !== this.currentItem) this.setActiveItem(currentItem);
        this.scrollTicking = false;
      });
    }

    updateScrollCue() {
      if (!this.scrollCue) return;

      const top = this.scrollContainer.scrollTop;
      const maxTop = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight;
      const isAtTop = top <= 8;
      const isAtBottom = top >= maxTop - 8;

      if (this.scrollCueText) {
        this.scrollCueText.textContent = isAtTop
          ? 'Scroll down for more'
          : isAtBottom
            ? 'Scroll up'
            : 'Scroll';
      }
    }

    scrollToItemPosition(item, smooth = true) {
      const centeredTop = item.offsetTop + (item.offsetHeight / 2) - (this.scrollContainer.clientHeight / 2);
      const maxTop = this.scrollContainer.scrollHeight - this.scrollContainer.clientHeight;
      this.scrollContainer.scrollTo({
        top: Math.max(0, Math.min(centeredTop, maxTop)),
        behavior: smooth ? 'smooth' : 'auto'
      });
    }

    scrollToItem(index) {
      const visibleItems = this.getVisibleItems();
      const item = visibleItems[Math.max(0, Math.min(index, visibleItems.length - 1))];
      if (!item) return;

      this.setActiveItem(item);
      this.scrollToItemPosition(item);
    }

    handleArrowClick(evt) {
      evt.preventDefault();
      const visibleItems = this.getVisibleItems();
      const currentIndex = Math.max(visibleItems.indexOf(this.currentItem), 0);
      const direction = evt.currentTarget.dataset.galleryDirection === 'previous' ? -1 : 1;
      this.scrollToItem(currentIndex + direction);
    }

    handleItemClick(evt) {
      if (!evt.target.closest('.gallery-viewer__item')) return;
      this.closest('modal-dialog')?.close();
    }

    handleKeyup(evt) {
      const visibleItems = this.getVisibleItems();
      const currentIndex = Math.max(visibleItems.indexOf(this.currentItem), 0);

      switch (evt.key) {
        case 'ArrowLeft':
          evt.preventDefault();
          this.scrollToItem(currentIndex - 1);
          break;
        case 'ArrowRight':
          evt.preventDefault();
          this.scrollToItem(currentIndex + 1);
          break;
        case 'Home':
          evt.preventDefault();
          this.scrollToItem(0);
          break;
        case 'End':
          evt.preventDefault();
          this.scrollToItem(visibleItems.length - 1);
          break;
      }
    }
  }

  customElements.define('gallery-viewer', GalleryViewer);
}
